/**
 * Workflow Templates — Pre-built workflow blueprints that users can drag onto the canvas.
 *
 * Each template defines a complete workflow: nodes[], edges[], suggested name, description.
 * Node IDs use a `tpl-` prefix; they're regenerated with unique IDs when applied.
 *
 * Layout convention (Y axis):
 *   Data Zone sits at y = -280 (DATA_ZONE_POSITION)
 *   Action nodes start at y ≈ 100, spaced ~180px apart vertically
 */

import { DATA_ZONE_POSITION, ZONE_SLOTS } from './DataZoneNode';

// ── Helpers ─────────────────────────────────────────────────────────────
const accountSlot = ZONE_SLOTS.find((s) => s.type === 'account_selector')!;
const ACCOUNT_POS = {
  x: DATA_ZONE_POSITION.x + accountSlot.x,
  y: DATA_ZONE_POSITION.y + accountSlot.y,
};

const brandSlot = ZONE_SLOTS.find((s) => s.type === 'brand_info')!;
const BRAND_POS = {
  x: DATA_ZONE_POSITION.x + brandSlot.x,
  y: DATA_ZONE_POSITION.y + brandSlot.y,
};

const scmSlot = ZONE_SLOTS.find((s) => s.type === 'seeding_contents_manager')!;
const SCM_POS = {
  x: DATA_ZONE_POSITION.x + scmSlot.x,
  y: DATA_ZONE_POSITION.y + scmSlot.y,
};

// Standard action-node X position (centered below Data Zone)
const ACTION_X = 80;
const startY = (idx: number) => 100 + idx * 180;

export interface SetupGuideStep {
  /** Step number label, e.g. "Bước 1" */
  step: string;
  /** Node display name */
  nodeLabel: string;
  /** Emoji icon for this step */
  icon: string;
  /** List of instruction bullets */
  instructions: string[];
  /** Optional highlighted tip */
  tip?: string;
}

export interface WorkflowTemplate {
  id: string;
  /** i18n key for the template name (resolved at render time) */
  nameKey: string;
  /** i18n key for a short description */
  descKey: string;
  /** Emoji icon for the card */
  icon: string;
  /** Suggested workflow name (i18n key) */
  suggestedNameKey: string;
  /** Category tags for visual grouping */
  tags: string[];
  /** Whether this template is disabled (coming soon) */
  disabled?: boolean;
  /** Node definitions (IDs are placeholders — re-keyed on apply) */
  nodes: TemplateNode[];
  /** Edge definitions referencing placeholder IDs */
  edges: TemplateEdge[];
  /** Whether this template creates multiple workflows */
  isMultiWorkflow?: boolean;
  /** Sub-workflow definitions for multi-workflow templates */
  workflows?: SubWorkflowTemplate[];
  /** Step-by-step configuration guide shown in modal after template is applied */
  setupGuide?: SetupGuideStep[];
}

export interface SubWorkflowTemplate {
  nameKey: string;
  nodes: TemplateNode[];
  edges: TemplateEdge[];
}

export interface TemplateNode {
  tplId: string;        // Placeholder ID (e.g. "tpl-account")
  type: string;         // Node type key (e.g. "post_to_profile")
  label: string;        // Display label
  position: { x: number; y: number };
  config: Record<string, any>;
}

export interface TemplateEdge {
  source: string; // tplId
  target: string; // tplId
}

// ═══════════════════════════════════════════════════════════════════════
// TEMPLATES
// ═══════════════════════════════════════════════════════════════════════

export const WORKFLOW_TEMPLATES: WorkflowTemplate[] = [
  // ── Video Repurposing Pipeline (Trend Discovery + Auto Production) ────────────────
  {
    id: 'omnichannel-video-repurposing',
    nameKey: 'tpl_videoRepurposing_name',
    descKey: 'tpl_videoRepurposing_desc',
    icon: '🎬',
    suggestedNameKey: 'tpl_videoRepurposing_workflowName',
    tags: ['video', 'trending', 'ai', 'repurpose'],
    nodes: [
      {
        tplId: 'tpl-account',
        type: 'account_selector',
        label: 'Select Accounts',
        position: ACCOUNT_POS,
        config: { platform: 'tiktok', selected_accounts: [], mode: 'all' },
      },
      {
        tplId: 'tpl-find-trend',
        type: 'find_trend_omnichannel',
        label: 'Find Trend Omnichannel',
        position: { x: ACTION_X, y: startY(0) },
        config: {
          platform: 'tiktok',
          keywords: ['viral', 'trending', 'review'],
          category: 'entertainment',
          max_results: 10,
          time_window_hours: 72,
          min_velocity_score: 50.0,
        },
      },
      {
        tplId: 'tpl-download-video',
        type: 'download_video',
        label: 'Download Video',
        position: { x: ACTION_X, y: startY(1) },
        config: {
          urls: [],
          max_duration: 180,
          resolution: '1080p',
        },
      },
      {
        tplId: 'tpl-analyze-viral',
        type: 'analyze_viral_content',
        label: 'Analyze Viral Content',
        position: { x: ACTION_X, y: startY(2) },
        config: {
          brand_name: 'BizTada',
          brand_tone: 'friendly',
          target_audience: 'mass',
          target_language: 'Tiếng Việt',
          custom_prompt: 'Phân tích hook và tạo kịch bản 60 giây tối ưu cho chuyển đổi',
        },
      },
      {
        tplId: 'tpl-auto-repurpose',
        type: 'auto_repurpose_video',
        label: 'Auto Repurpose Video',
        position: { x: ACTION_X, y: startY(3) },
        config: {
          voice: 'vi-VN-HoaiMyNeural',
          target_aspect_ratio: '9:16',
          bg_music_volume: 0.15,
          burn_subtitles: true,
          max_duration_sec: 90,
        },
      },
      {
        tplId: 'tpl-vault-upload',
        type: 'vault_upload_file',
        label: 'Vault Upload File',
        position: { x: ACTION_X, y: startY(4) },
        config: {
          goal: 'social',
          title: '',
          description: 'Auto-repurposed trending short video indexed into Telegram Vault',
        },
      },
    ],
    edges: [
      { source: 'tpl-account', target: 'tpl-find-trend' },
      { source: 'tpl-find-trend', target: 'tpl-download-video' },
      { source: 'tpl-download-video', target: 'tpl-analyze-viral' },
      { source: 'tpl-analyze-viral', target: 'tpl-auto-repurpose' },
      { source: 'tpl-auto-repurpose', target: 'tpl-vault-upload' },
    ],
    setupGuide: [
      {
        step: 'Bước 1',
        nodeLabel: 'Find Trend Omnichannel',
        icon: '🔍',
        instructions: [
          'Chọn nền tảng quét xu hướng (TikTok, YouTube Shorts, Douyin, Reels).',
          'Nhập từ khoá hoặc hashtag của thị trường mục tiêu.',
          'Đặt ngưỡng điểm lan truyền Min Velocity Score để lọc video bứt phá.',
        ],
        tip: 'Khung giờ quét 72h phù hợp nhất để đón đầu các trend đang tăng trưởng.',
      },
      {
        step: 'Bước 2',
        nodeLabel: 'Download Video & Phân Tích AI',
        icon: '🤖',
        instructions: [
          'Node Download Video tự động lấy link từ trend và trích xuất không watermark.',
          'Node Analyze Viral Content giải mã cấu trúc Hook - Body - CTA và viết kịch bản tái tạo.',
        ],
      },
      {
        step: 'Bước 3',
        nodeLabel: 'Auto Repurpose & Lưu Vault',
        icon: '🚀',
        instructions: [
          'Hệ thống lồng giọng đọc AI Edge-TTS và gõ phụ đề động Kinetic bắt mắt.',
          'File thành phẩm 9:16 lập tức tải lên Telegram Vault và hiển thị trong Brandlabs.',
        ],
      },
    ],
  },
  // ── Nuôi Nick (Account Nurturing) ────────────────────────────────
  {
    id: 'nuoi-nick',
    nameKey: 'tpl_nuoiNick_name',
    descKey: 'tpl_nuoiNick_desc',
    icon: '🐣',
    suggestedNameKey: 'tpl_nuoiNick_workflowName',
    tags: ['warmup', 'profile', 'friends'],
    nodes: [
      {
        tplId: 'tpl-account',
        type: 'account_selector',
        label: 'Select Accounts',
        position: ACCOUNT_POS,
        config: { platform: 'facebook', selected_accounts: [], mode: 'specific' },
      },
      {
        tplId: 'tpl-post-profile',
        type: 'post_to_profile',
        label: 'Post to Profile',
        position: { x: ACTION_X, y: startY(0) },
        config: { content_type: 'text', has_image: false, content_hints: 'Đăng bài nội dung hằng ngày để làm nóng profile', image_source_type: 'none', character_id: '' },
      },
      {
        tplId: 'tpl-interact',
        type: 'interact',
        label: 'Interact (Newsfeed/Reels)',
        position: { x: ACTION_X, y: startY(1) },
        config: { type: 'all', duration_minutes: 5, max_likes: 10, max_shares: 2, max_comments: 3, keywords: ['kinh doanh', 'marketing', 'khởi nghiệp'] },
      },
      {
        tplId: 'tpl-check-msg',
        type: 'check_message',
        label: 'Check Messages',
        position: { x: ACTION_X, y: startY(2) },
        config: {},
      },
      {
        tplId: 'tpl-notifications',
        type: 'view_notifications',
        label: 'View Notifications',
        position: { x: ACTION_X, y: startY(3) },
        config: { max_notifications: 10, mark_as_read: false },
      },
      {
        tplId: 'tpl-accept-friends',
        type: 'accept_friend_requests',
        label: 'Accept Friend Requests (Passive)',
        position: { x: ACTION_X, y: startY(4) },
        config: { max_accepts: 5 },
      },
    ],
    edges: [
      { source: 'tpl-account', target: 'tpl-post-profile' },
      { source: 'tpl-post-profile', target: 'tpl-interact' },
      { source: 'tpl-interact', target: 'tpl-check-msg' },
      { source: 'tpl-check-msg', target: 'tpl-notifications' },
      { source: 'tpl-notifications', target: 'tpl-accept-friends' },
    ],
    setupGuide: [
      {
        step: 'Bước 1',
        nodeLabel: 'Select Accounts',
        icon: '👤',
        instructions: [
          'Chọn tài khoản Facebook cá nhân cần được nuôi dưỡng (không chọn fanpage).',
          'Nên chọn các tài khoản mới hoặc ít hoạt động để làm nóng dần.',
          'Có thể chọn nhiều tài khoản cùng lúc, mỗi tài khoản sẽ chạy workflow riêng biệt.',
        ],
      },
      {
        step: 'Bước 2',
        nodeLabel: 'Post to Profile',
        icon: '📝',
        instructions: [
          'Gắn Character ID (từ BrandLabs) để bài viết tự động theo đúng giọng văn (Persona) và lấy avatar nhân vật.',
          'Hoặc nhập gợi ý nội dung (Content Hints) — AI sẽ tự tạo bài viết theo chủ đề tự nhiên.',
          'Ví dụ: "Chia sẻ câu chuyện kinh doanh, trải nghiệm thực tế."',
        ],
      },
      {
        step: 'Bước 3',
        nodeLabel: 'Interact (Newsfeed/Reels)',
        icon: '👍',
        instructions: [
          'Nhập từ khóa (Keywords) tạo vết thuật toán — Worker sẽ tìm kiếm trên Watch/Reels và tương tác để Facebook gán nhãn tệp sở thích đúng ngách.',
          'Cấu hình thời gian lướt feed (duration_minutes) — mặc định 5 phút.',
          'Chỉnh số like tối đa (max_likes), share (max_shares) và comment (max_comments) cho tự nhiên.',
        ],
      },
      {
        step: 'Bước 4',
        nodeLabel: 'Check Messages',
        icon: '✉️',
        instructions: [
          'Node này tự động kiểm tra và xem tin nhắn như người dùng thực sự.',
          'Không cần cấu hình thêm.',
        ],
      },
      {
        step: 'Bước 5',
        nodeLabel: 'View Notifications',
        icon: '🔔',
        instructions: [
          'Cấu hình số thông báo tối đa cần xem (max_notifications) — mặc định 10.',
          'Có thể bật "mark_as_read" nếu muốn đánh dấu đã đọc sau khi xem.',
        ],
      },
      {
        step: 'Bước 6',
        nodeLabel: 'Accept Friend Requests',
        icon: '🤝',
        instructions: [
          'Cấu hình số lời mời kết bạn tối đa chấp nhận mỗi lần chạy (max_accepts) — mặc định 5.',
          'Nên giữ số nhỏ (3–5) để tránh bị Facebook đánh dấu bất thường.',
        ],
        tip: 'Nên chạy workflow này mỗi ngày 1–2 lần trong những tuần đầu để tài khoản mới đạt độ tin cậy tự nhiên nhất.',
      },
    ],
  },

  // ── Đăng bài lên UID và Seeding ───────────────────────────────────
  {
    id: 'post-uid-seeding',
    nameKey: 'tpl_postUidSeeding_name',
    descKey: 'tpl_postUidSeeding_desc',
    icon: '📝',
    suggestedNameKey: 'tpl_postUidSeeding_workflowName',
    tags: ['profile', 'groups', 'seeding'],
    nodes: [
      {
        tplId: 'tpl-account',
        type: 'account_selector',
        label: 'Select Accounts',
        position: ACCOUNT_POS,
        config: { platform: 'facebook', selected_accounts: [], mode: 'specific' },
      },
      {
        tplId: 'tpl-post-profile',
        type: 'post_to_profile',
        label: 'Post to Profile',
        position: { x: -100, y: startY(0) },
        config: { content_type: 'text', has_image: false, content_hints: '', image_source_type: 'none', add_to_seeding_contents: true, seeding_folder_ids: [] },
      },
      {
        tplId: 'tpl-post-group',
        type: 'post_to_group',
        label: 'Post to Group',
        position: { x: 260, y: startY(0) },
        config: { group_urls: [], content_hints: '', has_image: false, image_source_type: 'none', post_mode: 'discussion', add_to_seeding_contents: true, seeding_folder_ids: [] },
      },
    ],
    edges: [
      { source: 'tpl-account', target: 'tpl-post-profile' },
      { source: 'tpl-account', target: 'tpl-post-group' },
    ],
    setupGuide: [
      {
        step: 'Chuẩn bị',
        nodeLabel: 'Seeding Contents Manager',
        icon: '📂',
        instructions: [
          'Trước khi chạy, hãy vào trang Seeding Content (📂 biểu tượng trên thanh công cụ) và tạo một thư mục mới.',
          'Ví dụ: đặt tên thư mục là "Năng lượng mặt trời" để dễ phân biệt.',
          'URL các bài viết đăng từ workflow này sẽ tự động được lưu vào thư mục bạn chọn.',
        ],
        tip: 'Cần tạo thư mục trong Seeding Content trước — nếu chưa có sẽ không lưu được URL bài viết.',
      },
      {
        step: 'Bước 1',
        nodeLabel: 'Select Accounts',
        icon: '👤',
        instructions: [
          'Chọn các tài khoản cá nhân Facebook sẽ thực hiện đăng bài.',
          'Mỗi tài khoản sẽ đăng song song lên cả profile lẫn group.',
        ],
      },
      {
        step: 'Bước 2',
        nodeLabel: 'Post to Profile',
        icon: '📰',
        instructions: [
          'Nhập gợi ý nội dung (Content Hints) — AI sẽ tự tạo bài viết.',
          'Bật "Thêm vào Seeding Content" → chọn thư mục bạn vừa tạo ở bước chuẩn bị.',
          'Khi chạy, URL bài viết sẽ tự động được đẩy vào kho seeding.',
        ],
      },
      {
        step: 'Bước 3',
        nodeLabel: 'Post to Group',
        icon: '👥',
        instructions: [
          'Nhập URL các group muốn đăng vào ô Group URLs (mỗi URL một dòng).',
          'Nhập gợi ý nội dung riêng cho group nếu muốn khác profile.',
          'Bật "Thêm vào Seeding Content" → chọn cùng thư mục như bước 2 để tổng hợp URL.',
        ],
      },
    ],
  },

  // ── Kết bạn với người tương tác bài viết ─────────────────────────
  {
    id: 'add-friends-interactions',
    nameKey: 'tpl_addFriendsInteractions_name',
    descKey: 'tpl_addFriendsInteractions_desc',
    icon: '🤝',
    suggestedNameKey: 'tpl_addFriendsInteractions_workflowName',
    tags: ['friends', 'network', 'add-friend'],
    nodes: [
      {
        tplId: 'tpl-account',
        type: 'account_selector',
        label: 'Select Accounts',
        position: ACCOUNT_POS,
        config: { platform: 'facebook', selected_accounts: [], mode: 'specific' },
      },
      {
        tplId: 'tpl-find-top-posts',
        type: 'find_top_posts',
        label: 'Find Top Posts',
        position: { x: ACTION_X, y: startY(0) },
        config: { source_urls: [], days_back: 7, top_n: 1, label: '' },
      },
      {
        tplId: 'tpl-search-add-friend',
        type: 'search_add_friend',
        label: 'Search & Add Friend (Active)',
        position: { x: ACTION_X, y: startY(1) },
        config: { type: 'from_top_posts', keywords: [], urls: null, max_add: 3 },
      },
    ],
    edges: [
      { source: 'tpl-account', target: 'tpl-find-top-posts' },
      { source: 'tpl-find-top-posts', target: 'tpl-search-add-friend' },
    ],
    setupGuide: [
      {
        step: 'Bước 1',
        nodeLabel: 'Select Accounts',
        icon: '👤',
        instructions: [
          'Chọn tài khoản cá nhân Facebook sẽ gửi lời mời kết bạn.',
          'Nên dùng tài khoản có profile đầy đủ và đã nuôi dưỡng để tăng tỷ lệ đồng ý.',
        ],
      },
      {
        step: 'Bước 2',
        nodeLabel: 'Find Top Posts',
        icon: '🔥',
        instructions: [
          'Nhập danh sách URL nguồn (group, profile, fanpage) cần quét — mỗi URL một dòng.',
          'Cấu hình "Days Back" — số ngày quét lại (mặc định 7 ngày).',
          'Cấu hình "Top N" — số bài viết hot nhất cần lấy từ mỗi nguồn (mặc định 1).',
          'Tùy chọn: điền nhãn (Label) để dễ quản lý khi có nhiều workflow.',
        ],
      },
      {
        step: 'Bước 3',
        nodeLabel: 'Search & Add Friend',
        icon: '🤝',
        instructions: [
          'Chế độ "from_top_posts" đã được cấu hình sẵn — không cần thay đổi.',
          'Chỉnh số lượng lời mời tối đa (max_add) mỗi lần chạy — không nên vượt quá 5.',
        ],
        tip: 'Chạy workflow tối đa 2–3 lần/ngày và không gửi quá 10 lời mời/ngày để tránh bị Facebook hạn chế.',
      },
    ],
  },

  // ── Seeding bài viết ──────────────────────────────────────────────
  {
    id: 'seeding-bai-viet',
    nameKey: 'tpl_seedingBaiViet_name',
    descKey: 'tpl_seedingBaiViet_desc',
    icon: '💬',
    suggestedNameKey: 'tpl_seedingBaiViet_workflowName',
    tags: ['seeding', 'comments', 'likes', 'SCM'],
    nodes: [
      {
        tplId: 'tpl-account',
        type: 'account_selector',
        label: 'Select Accounts',
        position: ACCOUNT_POS,
        config: { platform: 'facebook', selected_accounts: [], mode: 'specific' },
      },
      {
        tplId: 'tpl-scm',
        type: 'seeding_contents_manager',
        label: 'Seeding Contents Manager',
        position: SCM_POS,
        config: { name: 'Seeding for bài viết', seeding_accounts: [] },
      },
      {
        tplId: 'tpl-comment',
        type: 'comment_on_post',
        label: 'Comment on Post',
        position: { x: -100, y: startY(0) },
        config: { post_url: '', content_hints: '', comment_mode: 'discussion', dispatch_mode: 'managed', seeding_contents_manager_node_id: 'tpl-scm' },
      },
      {
        tplId: 'tpl-like',
        type: 'like_posts',
        label: 'Like Posts',
        position: { x: 260, y: startY(0) },
        config: { target: 'newsfeed', max_likes: 10, urls: [], dispatch_mode: 'managed', seeding_contents_manager_node_id: 'tpl-scm' },
      },
    ],
    edges: [
      { source: 'tpl-account', target: 'tpl-comment' },
      { source: 'tpl-account', target: 'tpl-like' },
    ],
    setupGuide: [
      {
        step: 'Chuẩn bị',
        nodeLabel: 'Seeding Contents Manager',
        icon: '📂',
        instructions: [
          'Vào trang Seeding Content (📂 biểu tượng trên thanh công cụ) và tạo thư mục chứa URL bài viết cần seeding.',
          'Thư mục này sẽ được nối vào node Seeding Contents Manager để các tài khoản seeding biết cần comment/like bài nào.',
          'Đảm bảo trong thư mục đã có ít nhất vài URL bài viết trước khi chạy.',
        ],
        tip: 'Nếu chưa có bài viết trong kho seeding, workflow sẽ không thực hiện được gì cả.',
      },
      {
        step: 'Bước 1',
        nodeLabel: 'Seeding Contents Manager',
        icon: '⚙️',
        instructions: [
          'Nhấn vào node SCM trên canvas để mở cấu hình.',
          'Đặt tên cho quản lý seeding này (ví dụ: "Seeding bài quảng cáo").',
          'Thêm seeding accounts: chọn tài khoản hoặc fanpage nguồn bài viết, chọn thư mục seeding.',
          'Cấu hình tần suất: số lần seeding tối đa mỗi ngày/bài và thời gian chờ giữa các lần.',
        ],
      },
      {
        step: 'Bước 2',
        nodeLabel: 'Select Accounts',
        icon: '👤',
        instructions: [
          'Chọn các tài khoản seeding — đây là tài khoản sẽ comment và like bài viết.',
          'Không nên dùng chính tài khoản đăng bài để seeding cho bài viết đó.',
        ],
      },
      {
        step: 'Bước 3',
        nodeLabel: 'Comment on Post',
        icon: '💬',
        instructions: [
          'Chế độ Dispatch phải đặt là "Quản lí (qua SCM)" — đã cấu hình sẵn, kiểm tra lại.',
          'Nhập gợi ý nội dung comment (content_hints) phù hợp với bài viết.',
          'Cấu hình phong cách comment: tông giọng, độ dài, emoji.',
        ],
      },
      {
        step: 'Bước 4',
        nodeLabel: 'Like Posts',
        icon: '👍',
        instructions: [
          'Chế độ Dispatch phải đặt là "Quản lí (qua SCM)" — đã cấu hình sẵn, kiểm tra lại.',
          'Số like tối đa mỗi lần chạy (max_likes) — mặc định 10.',
        ],
        tip: 'Tỷ lệ comment:like nên khoảng 1:3 để phân phối tương tác tự nhiên hơn.',
      },
    ],
  },

  // ── Chăm sóc Fanpage + Reply + Private Reply ──────────────────────
  {
    id: 'fanpage-care-reply-inbox',
    nameKey: 'tpl_fanpageCareReplyInbox_name',
    descKey: 'tpl_fanpageCareReplyInbox_desc',
    icon: '🛎️',
    suggestedNameKey: 'tpl_fanpageCareReplyInbox_workflowName',
    tags: ['fanpage', 'comments', 'inbox', 'reply'],
    nodes: [
      {
        tplId: 'tpl-account',
        type: 'account_selector',
        label: 'Select Accounts',
        position: ACCOUNT_POS,
        config: { platform: 'facebook', selected_fanpages: [], mode: 'specific' },
      },
      {
        tplId: 'tpl-post-fanpage',
        type: 'post_to_fanpage',
        label: 'Post to Fanpage',
        position: { x: ACTION_X, y: startY(0) },
        config: { page_ids: [], content_type: 'text', content_hints: '', image_source_type: 'none', add_to_seeding_contents: true, seeding_folder_ids: [], schedule_mode: 'all' },
      },
      {
        tplId: 'tpl-fanpage-reply',
        type: 'fanpage_reply_to_comments',
        label: 'Fanpage Reply to Comments',
        position: { x: ACTION_X, y: startY(1) },
        config: { page_ids: [], target_post_mode: 'specific_posts', post_ids: [], max_posts_to_scan: 5, feed_fetch_limit: 10, schedule_mode: 'all', max_replies: 2, max_comments: 20, content_hints: 'Reply rằng fanpage đã gửi tin nhắn đến bạn, mời bạn check ib', comment_style: {}, reply_mode: 'keyword', reply_keywords: ['ib'] },
      },
      {
        tplId: 'tpl-fanpage-private',
        type: 'fanpage_private_reply',
        label: 'Fanpage Private Reply',
        position: { x: ACTION_X, y: startY(2) },
        config: { page_ids: [], target_post_mode: 'specific_posts', post_ids: [], max_posts_to_scan: 3, feed_fetch_limit: 10, schedule_mode: 'all', message_source_mode: 'manual', manual_message: '', comment_filter_mode: 'keyword', comment_keywords: ['ib'], max_messages_per_post: 10, reply_max_per_keyword: 1 },
      },
    ],
    edges: [
      { source: 'tpl-account', target: 'tpl-post-fanpage' },
      { source: 'tpl-post-fanpage', target: 'tpl-fanpage-reply' },
      { source: 'tpl-fanpage-reply', target: 'tpl-fanpage-private' },
    ],
    setupGuide: [
      {
        step: 'Bước 1',
        nodeLabel: 'Select Accounts',
        icon: '📱',
        instructions: [
          'Chọn Fanpage muốn quản lí trong ô "Fanpages" (không chọn ô Accounts).',
          'Fanpage phải được kết nối tới hệ thống trước — vào trang Fanpage Management để kết nối nếu chưa có.',
        ],
      },
      {
        step: 'Bước 2',
        nodeLabel: 'Post to Fanpage',
        icon: '📰',
        instructions: [
          'Chọn Page ID từ fanpage đã kết nối.',
          'Nhập gợi ý nội dung (content_hints) — AI sẽ tự tạo bài đăng lên trang.',
          'Tùy chọn: bật "Thêm vào Seeding Content" để lưu URL bài mới đăng vào kho seeding.',
        ],
      },
      {
        step: 'Bước 3',
        nodeLabel: 'Fanpage Reply to Comments',
        icon: '💬',
        instructions: [
          'Chọn bài viết cụ thể muốn theo dõi: nhập URL bài hoặc Post ID.',
          'Nhập keyword trigger — ví dụ: "ib" — chỉ reply các comment chứa keyword này.',
          'Nhập gợi ý nội dung reply — ví dụ: "Fanpage đã gửi tin nhắn đến bạn, mời check ib."',
          'Cấu hình số comment tối đa (max_comments) và reply tối đa (max_replies) mỗi lần chạy.',
        ],
      },
      {
        step: 'Bước 4',
        nodeLabel: 'Fanpage Private Reply',
        icon: '✉️',
        instructions: [
          'Chọn cùng bài viết và keyword như bước 3.',
          'Nhập nội dung tin nhắn private (manual_message) gửi cho khách.',
          'Cấu hình số tin tối đa mỗi bài (max_messages_per_post) và tối đa theo keyword (reply_max_per_keyword).',
        ],
        tip: 'Quy trình: Khách comment "ib" → Fanpage reply công khai rằng đã inbox → đồng thời gửi tin nhắn riêng.',
      },
    ],
  },

  // ── Theo dõi bài viết → Auto Inbox khi có keyword ────────────────
  {
    id: 'post-auto-responder',
    nameKey: 'tpl_postAutoResponder_name',
    descKey: 'tpl_postAutoResponder_desc',
    icon: '🔔',
    suggestedNameKey: 'tpl_postAutoResponder_workflowName',
    tags: ['inbox', 'keyword', 'auto-reply', 'monitor'],
    nodes: [
      {
        tplId: 'tpl-account',
        type: 'account_selector',
        label: 'Select Accounts',
        position: ACCOUNT_POS,
        config: { platform: 'facebook', selected_accounts: [], mode: 'specific' },
      },
      {
        tplId: 'tpl-auto-responder',
        type: 'post_auto_responder',
        label: 'Post Auto Responder',
        position: { x: ACTION_X, y: startY(0) },
        config: { post_url: '', keywords: ['inbox', 'ib'], message: 'Mình đã inbox bạn, bạn check tin nhắn nhé! 😊', reply_text: 'Mình đã inbox bạn nhé!' },
      },
    ],
    edges: [
      { source: 'tpl-account', target: 'tpl-auto-responder' },
    ],
    setupGuide: [
      {
        step: 'Bước 1',
        nodeLabel: 'Select Accounts',
        icon: '👤',
        instructions: [
          'Chọn tài khoản Facebook cá nhân sở hữu bài viết cần theo dõi.',
          'Tài khoản này phải là người đăng bài hoặc có quyền truy cập bài viết đó.',
        ],
      },
      {
        step: 'Bước 2',
        nodeLabel: 'Post Auto Responder',
        icon: '🤖',
        instructions: [
          'Nhập URL bài viết cần theo dõi (post_url) — bài đăng trên profile hoặc group.',
          'Nhập danh sách keyword kích hoạt — ví dụ: "ib", "inbox", "giá", "mua" (mỗi keyword cách nhau bằng dấu phẩy).',
          'Nhập nội dung tin nhắn private (message) gửi cho khách khi họ comment đúng keyword.',
          'Nhập text reply công khai (reply_text) vào comment — ví dụ: "Mình đã inbox bạn nhé!"',
        ],
        tip: 'Workflow này lý tưởng cho các bài đăng bán hàng: khách bình luận "ib" → hệ thống tự reply + inbox ngay lập tức, không bỏ sót khách nào.',
      },
    ],
  },

  // ── Tìm bài viết → Reply Comment theo keyword ─────────────────────
  {
    id: 'find-posts-keyword-reply',
    nameKey: 'tpl_findPostsKeywordReply_name',
    descKey: 'tpl_findPostsKeywordReply_desc',
    icon: '🔍',
    suggestedNameKey: 'tpl_findPostsKeywordReply_workflowName',
    tags: ['groups', 'keyword', 'reply', 'comments'],
    nodes: [
      {
        tplId: 'tpl-account',
        type: 'account_selector',
        label: 'Select Accounts',
        position: ACCOUNT_POS,
        config: { platform: 'facebook', selected_accounts: [], mode: 'specific' },
      },
      {
        tplId: 'tpl-find-group-posts',
        type: 'find_group_posts',
        label: 'Find Group Posts',
        position: { x: ACTION_X, y: startY(0) },
        config: { group_urls: [], search_mode: 'keyword', criteria: '', days_back: 14, requirement: '', max_scrolls_per_group: 1 },
      },
      {
        tplId: 'tpl-reply',
        type: 'reply_to_comments',
        label: 'Reply to Comments',
        position: { x: ACTION_X, y: startY(1) },
        config: { post_url: '', max_replies: 1, max_comments: 20, content_hints: '', comment_style: { emoji_usage: 'none', length: 'short', tone: 'friendly' }, dispatch_mode: 'group_posts', find_group_posts_node_id: 'tpl-find-group-posts', reply_mode: 'keyword', reply_keywords: [], reply_max_per_keyword: 0 },
      },
    ],
    edges: [
      { source: 'tpl-account', target: 'tpl-find-group-posts' },
      { source: 'tpl-find-group-posts', target: 'tpl-reply' },
    ],
    setupGuide: [
      {
        step: 'Bước 1',
        nodeLabel: 'Select Accounts',
        icon: '👤',
        instructions: [
          'Chọn tài khoản cá nhân Facebook sẽ thực hiện tìm kiếm và reply comment.',
          'Tài khoản phải là thành viên của group cần tìm kiếm.',
        ],
      },
      {
        step: 'Bước 2',
        nodeLabel: 'Find Group Posts',
        icon: '🔍',
        instructions: [
          'Nhập URL các group muốn tìm kiếm (mỗi URL một dòng).',
          'Chọn chế độ "keyword" và nhập từ khóa tìm kiếm — hệ thống sẽ quét các bài có chứa từ khóa này.',
          'Cấu hình "Days Back" — số ngày quét lại (mặc định 14 ngày).',
          'Cấu hình "Max Scrolls" — số lần cuộn xuống trong mỗi group khi quét.',
        ],
      },
      {
        step: 'Bước 3',
        nodeLabel: 'Reply to Comments',
        icon: '💬',
        instructions: [
          'Chế độ "Dispatch" phải được đặt là "group_posts" — đã cấu hình sẵn, kiểm tra lại.',
          'Nhập gợi ý nội dung reply (content_hints) — AI sẽ tự tạo nội dung reply phù hợp.',
          'Cấu hình số comment tối đa cần reply (max_comments) và số reply tối đa mỗi bài (max_replies).',
          'Chọn phong cách: tông giọng, độ dài, emoji.',
        ],
        tip: 'Mẹo: đặt reply_mode là "keyword" nếu chỉ muốn reply các comment chứa từ khóa cụ thể, thay vì reply tất cả comment.',
      },
    ],
  },

];

// ── Apply Template Helper ───────────────────────────────────────────────
// Converts a template's placeholder IDs into unique runtime IDs and returns
// nodes[] + edges[] ready for the workflow store.

import { Node, Edge } from '@xyflow/react';
import { DATA_ZONE_ID } from './DataZoneNode';

export function applyTemplate(template: WorkflowTemplate): { nodes: Node[]; edges: Edge[] } {
  const ts = Date.now();
  const idMap = new Map<string, string>();

  // Map placeholder IDs → unique IDs
  template.nodes.forEach((n, i) => {
    idMap.set(n.tplId, `node-${ts}-${i}`);
  });

  // Create data zone node
  const zoneNode: Node = {
    id: DATA_ZONE_ID,
    type: 'data_zone',
    position: { ...DATA_ZONE_POSITION },
    data: { label: 'Data Injection', isZone: true },
    selectable: false,
    draggable: false,
    deletable: false,
    style: { zIndex: -1 },
  };

  // Create action nodes
  const actionNodes: Node[] = template.nodes.map((n) => ({
    id: idMap.get(n.tplId)!,
    type: n.type,
    position: { ...n.position },
    data: {
      label: n.label,
      type: n.type,
      [`${n.type}_config`]: { ...n.config },
    },
  }));

  // Create edges
  const edges: Edge[] = template.edges.map((e, i) => ({
    id: `edge-${ts}-${i}`,
    source: idMap.get(e.source)!,
    target: idMap.get(e.target)!,
  }));

  return {
    nodes: [zoneNode, ...actionNodes],
    edges,
  };
}

// ── Apply Multi-Workflow Template ───────────────────────────────────────
// Returns an array of { name, nodes[], edges[] } — one per sub-workflow.
export function applyMultiWorkflowTemplate(
  template: WorkflowTemplate,
  translateFn: (key: string) => string,
): { name: string; nodes: Node[]; edges: Edge[] }[] {
  if (!template.workflows) return [];

  return template.workflows.map((sub, wIdx) => {
    const ts = Date.now() + wIdx;
    const idMap = new Map<string, string>();

    sub.nodes.forEach((n, i) => {
      idMap.set(n.tplId, `node-${ts}-${i}`);
    });

    const zoneNode: Node = {
      id: DATA_ZONE_ID,
      type: 'data_zone',
      position: { ...DATA_ZONE_POSITION },
      data: { label: 'Data Injection', isZone: true },
      selectable: false,
      draggable: false,
      deletable: false,
      style: { zIndex: -1 },
    };

    const actionNodes: Node[] = sub.nodes.map((n) => ({
      id: idMap.get(n.tplId)!,
      type: n.type,
      position: { ...n.position },
      data: {
        label: n.label,
        type: n.type,
        [`${n.type}_config`]: { ...n.config },
      },
    }));

    const edges: Edge[] = sub.edges.map((e, i) => ({
      id: `edge-${ts}-${i}`,
      source: idMap.get(e.source)!,
      target: idMap.get(e.target)!,
    }));

    return {
      name: translateFn(sub.nameKey),
      nodes: [zoneNode, ...actionNodes],
      edges,
    };
  });
}

